import React, { Component } from 'react';
import { Field, FieldArray } from 'redux-form';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import { Alert, Button, FieldError } from '@/ui';
import NativeSelectField from '../FormFields/NativeSelectField';
import { TabPane } from './TabPane';
import MultiSelectAutocomplete from '@/components/FormFields/MultiSelectAutocomplete';
import _ from 'lodash';
import { useAffiliates } from '../../views/OfferBlocks/queries';
import { renderFailureProbabilityInput } from './FailureProbabilityInput';
import { MultiSelect as CriteriaMultiSelect } from './CriteriaPanel/FormComponents';
import { qualityScoreDayFilterOptions } from './CriteriaPanel/constants';

/** Labels sit above their control, matching the other panes in this tree. */
const LABEL_SX = { display: 'block', mb: 0.5 };

/** Reserves no layout space, so a validation message appearing never shifts the row. */
const FLOATING_ERROR_SX = {
  position: 'absolute',
  top: '100%',
  left: 0,
  whiteSpace: 'nowrap',
  fontSize: '0.875rem',
  m: 0,
};

/** Stops the scroll wheel nudging a number field's value while the page scrolls past it. */
const blurOnWheel = (event) => event.currentTarget.blur();

/* ------------------------------------------------------------------ */
/* Custom renderMultiSelect that handles ID-to-object conversion */
/* WITHOUT the extra <br /> that causes alignment issues */
/* ------------------------------------------------------------------ */
const renderAffiliateMultiSelect = ({ input, data, busy, meta, ...props }) => {
  // Convert numeric IDs to affiliate objects when form loads
  const convertIdsToObjects = (value) => {
    if (!value || !Array.isArray(value)) return value;

    return value.map((item) => {
      // If it's already an object with id and name, return as is
      if (typeof item === 'object' && item.id && item.name) {
        return item;
      }

      // If it's a numeric ID, find the corresponding affiliate in data
      if (typeof item === 'number' || (typeof item === 'string' && !isNaN(item))) {
        const affiliateId = parseInt(item);
        const affiliate = data?.find((aff) => aff.id === affiliateId);
        if (affiliate) {
          return affiliate;
        }
      }

      // Return as is if we can't convert it
      return item;
    });
  };

  const convertedValue = convertIdsToObjects(input.value);
  const { touched, error } = meta || {};

  return (
    <>
      <MultiSelectAutocomplete
        name={input.name}
        value={convertedValue || []}
        onChange={input.onChange}
        onBlur={() => input.onBlur()}
        options={data}
        busy={busy}
        {...props}
      />
      <FieldError error={error} touched={touched} />
    </>
  );
};

/* ------------------------------------------------------------------ */
/* Inline picker that mirrors AttributePane's "Affiliate Names" field  */
/* ------------------------------------------------------------------ */
const AffiliatesIdsOrTagsPicker = ({ name, placeholder = 'Select affiliates' }) => {
  const { data: affiliates, isLoading: affiliatesLoading } = useAffiliates(true, (data) =>
    _.orderBy(data, ['name'], ['asc']).map(({ name, networkAffiliateId }) => ({
      id: networkAffiliateId,
      name,
    }))
  );

  const loading = affiliatesLoading;

  const affiliateNames = loading ? [{ name: '[None]', id: null }] : [...(affiliates || [])];

  return (
    <Field
      name={name}
      component={renderAffiliateMultiSelect}
      placeholder={placeholder}
      data={affiliateNames}
      busy={loading}
      dataKey='name'
      textField={(item) => (item?.name?.startsWith('#') ? `${item.id}` : `${item.id}: ${item.name}`)}
      groupBy={(item) => (item?.name?.startsWith('#') ? 0 : 1)}
      renderListGroup={({ group }) => (group === 0 ? <span>Tags</span> : <span>Affiliates</span>)}
      style={{ width: '100%' }}
    />
  );
};

class AffiliateQualityScorePane extends Component {
  state = {
    nextRangeIndex: 0,
    nextBypassIndex: 0,
    maxRangeIndex: -1,
    maxBypassIndex: -1,
    lastUpdateTime: null,
  };

  componentDidMount() {
    // Initialize max indices based on existing data
    this.initializeMaxIndices();
  }

  componentDidUpdate(prevProps) {
    // Update max indices when props change (e.g., when form data loads)
    if (prevProps.qualityScoreCriteria !== this.props.qualityScoreCriteria) {
      this.initializeMaxIndices();
    }

    // Force re-render when currentCaps updates
    if (prevProps.currentCaps !== this.props.currentCaps) {
      this.setState({ lastUpdateTime: Date.now() });
    }
  }

  initializeMaxIndices = () => {
    const { qualityScoreCriteria } = this.props;

    // Initialize range max index
    if (qualityScoreCriteria?.qualityScoreRange && Array.isArray(qualityScoreCriteria.qualityScoreRange)) {
      const maxRangeIndex = Math.max(
        ...qualityScoreCriteria.qualityScoreRange.map((item) => {
          const match = item.name?.match(/index_(\d+)/);
          return match ? parseInt(match[1]) : -1;
        })
      );
      if (maxRangeIndex >= 0 && maxRangeIndex > this.state.maxRangeIndex) {
        this.setState({
          maxRangeIndex: maxRangeIndex,
          nextRangeIndex: maxRangeIndex + 1,
        });
      }
    }

    // Initialize bypass max index
    if (qualityScoreCriteria?.bypass && Array.isArray(qualityScoreCriteria.bypass)) {
      const maxBypassIndex = Math.max(
        ...qualityScoreCriteria.bypass.map((item) => {
          const match = item.name?.match(/index_(\d+)/);
          return match ? parseInt(match[1]) : -1;
        })
      );
      if (maxBypassIndex >= 0 && maxBypassIndex > this.state.maxBypassIndex) {
        this.setState({
          maxBypassIndex: maxBypassIndex,
          nextBypassIndex: maxBypassIndex + 1,
        });
      }
    }
  };

  /* -------------------- Ranges (min/max) section ------------------- */
  pushNewRangeRow = (fields) => {
    const indexToUse = this.state.maxRangeIndex + 1;
    fields.push({
      name: `index_${indexToUse}`,
      minScore: null,
      maxScore: null,
      cap: null,
      failureProbability: null,
      validMultipliers: 1,
    });
    this.setState({
      nextRangeIndex: indexToUse + 1,
      maxRangeIndex: indexToUse,
    });
  };

  removeRangeRow = (fields, index) => {
    // Always allow deletion
    fields.remove(index);
  };

  validateValidMultipliers = (value) => {
    if (value === null || value === undefined || value === '') {
      return 'Must be 0 or greater';
    }
    const numValue = parseInt(value, 10);
    if (isNaN(numValue) || numValue < 0) {
      return 'Must be 0 or greater';
    }
    return undefined;
  };

  validateFailureProbability = (value) => {
    if (value === null || value === undefined || value === '') {
      return undefined; // Allow empty values
    }
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < 0 || numValue >= 1) {
      return 'Must be between 0.00 and 0.99';
    }
    return undefined;
  };

  renderNumberInput = ({
    input,
    meta: { touched, error },
    id,
    placeholder,
    min,
    max,
    step,
    disabled,
    readOnly,
    type = 'number',
  }) => (
    <>
      <TextField
        {...input}
        id={id}
        type={type}
        placeholder={placeholder}
        disabled={disabled}
        error={touched && Boolean(error)}
        slotProps={{ input: { readOnly }, htmlInput: { min, max, step, onWheel: blurOnWheel } }}
      />
      <FormHelperText error sx={FLOATING_ERROR_SX}>
        {(() => {
          if (!touched || !error) return '\u00A0';
          if (typeof error === 'string') return error;
          if (typeof error === 'object' && error.message) return error.message;
          return '\u00A0';
        })()}
      </FormHelperText>
    </>
  );

  renderSelectInput = ({ input, meta: { touched, error }, id, options = [], disabled }) => (
    <>
      <NativeSelectField
        {...input}
        id={id}
        disabled={disabled}
        error={touched && Boolean(error)}
        onChange={(event) => input.onChange(event.target.value)}
      >
        {options.map((option) => {
          const value = option.value ?? option.key ?? option.option;
          const label = option.option ?? option.label ?? String(value ?? '');
          return (
            <option key={value ?? label} value={value}>
              {label}
            </option>
          );
        })}
      </NativeSelectField>
      <FieldError error={error} touched={touched} />
    </>
  );

  renderRangeRows = ({ fields, meta }) => {
    const { currentCaps, qualityScoreCriteria } = this.props;

    return (
      <Box component='section' sx={{ mb: 1.5 }}>
        <Typography variant='h6' sx={{ mb: 2 }}>
          Affiliate Quality Score Ranges
        </Typography>
        {meta && meta.error && (
          <Alert color='danger' sx={{ mb: 2 }}>
            {meta.error}
          </Alert>
        )}
        {fields.length > 0 ? (
          <Stack spacing={2}>
            {fields.map((fieldName, index) => {
              const fieldData = qualityScoreCriteria?.qualityScoreRange?.[index];
              const nameValue = fieldData?.name || `index_${index}`;
              const redisIndex = nameValue.replace('index_', '');
              const currentCapValue = currentCaps ? currentCaps.quality_score_range?.[redisIndex] || 0 : 0;
              const isLast = index === fields.length - 1;

              return (
                <Card key={fieldName}>
                  <CardContent>
                    <Stack
                      direction='row'
                      spacing={1}
                      sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
                    >
                      <Field
                        name={`${fieldName}.name`}
                        component={({ input }) => (
                          <Typography variant='body2'>{input.value || `index_${index}`}</Typography>
                        )}
                      />
                      <Stack direction='row' spacing={1}>
                        {isLast && (
                          <Button
                            color='primary'
                            type='button'
                            title='Add Quality Score Range'
                            startIcon={<Add />}
                            onClick={() => this.pushNewRangeRow(fields)}
                          >
                            Add Range
                          </Button>
                        )}
                        <Button
                          color='danger'
                          type='button'
                          title='Remove Quality Score Range'
                          startIcon={<Close />}
                          onClick={() => this.removeRangeRow(fields, index)}
                        >
                          Remove
                        </Button>
                      </Stack>
                    </Stack>
                    <Grid container spacing={2} sx={{ alignItems: 'end', pb: 3 }}>
                      <Grid size={{ xs: 12, md: 1 }}>
                        <FormLabel htmlFor={`${fieldName}-minScore`} sx={LABEL_SX}>
                          Min Score
                        </FormLabel>
                        <Field
                          name={`${fieldName}.minScore`}
                          component={this.renderNumberInput}
                          id={`${fieldName}-minScore`}
                          type='number'
                          placeholder='Min Score'
                          normalize={(value) => {
                            const parsed = parseInt(value, 10);
                            return Number.isNaN(parsed) ? value : Math.min(100, Math.max(0, parsed));
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 1 }}>
                        <FormLabel htmlFor={`${fieldName}-maxScore`} sx={LABEL_SX}>
                          Max Score
                        </FormLabel>
                        <Field
                          name={`${fieldName}.maxScore`}
                          component={this.renderNumberInput}
                          id={`${fieldName}-maxScore`}
                          type='number'
                          placeholder='Max Score'
                          normalize={(value) => {
                            const parsed = parseInt(value, 10);
                            return Number.isNaN(parsed) ? value : Math.min(100, Math.max(0, parsed));
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 1 }}>
                        <FormLabel htmlFor={`${fieldName}-cap`} sx={LABEL_SX}>
                          Cap Amount
                        </FormLabel>
                        <Field
                          name={`${fieldName}.cap`}
                          component={this.renderNumberInput}
                          id={`${fieldName}-cap`}
                          type='number'
                          placeholder='Infinity'
                          min={0}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 1 }}>
                        <FormLabel htmlFor={`${fieldName}-currentCap`} sx={LABEL_SX}>
                          Current Cap
                        </FormLabel>
                        <TextField
                          id={`${fieldName}-currentCap`}
                          value={currentCapValue}
                          disabled
                          slotProps={{ input: { readOnly: true } }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 2 }}>
                        <FormLabel htmlFor={`${fieldName}-failure`} sx={LABEL_SX}>
                          Failure Probability (%)
                        </FormLabel>
                        <Field
                          name={`${fieldName}.failureProbability`}
                          component={renderFailureProbabilityInput}
                          id={`${fieldName}-failure`}
                          placeholder='0%'
                          min={0}
                          max={0.99}
                          step='0.01'
                          validate={this.validateFailureProbability}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 2 }}>
                        <FormLabel htmlFor={`${fieldName}-validMultipliers`} sx={LABEL_SX}>
                          Minimum Valid Multipliers
                        </FormLabel>
                        <Field
                          name={`${fieldName}.validMultipliers`}
                          component={this.renderNumberInput}
                          id={`${fieldName}-validMultipliers`}
                          type='number'
                          placeholder='0'
                          min={0}
                          step={1}
                          validate={this.validateValidMultipliers}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Field
                          name={`${fieldName}.dayFilters`}
                          component={({ input }) => (
                            <CriteriaMultiSelect
                              label='Day Filter'
                              value={input.value || []}
                              onChange={input.onChange}
                              options={qualityScoreDayFilterOptions}
                              placeholder='All Days'
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        ) : (
          <Box sx={{ textAlign: 'center' }}>
            <Button
              color='primary'
              type='button'
              title='Add Quality Score Range'
              startIcon={<Add />}
              onClick={() => this.pushNewRangeRow(fields)}
            >
              Add Quality Score Range
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  /* -------------------- Bypass Affiliates section ------------------- */
  pushNewBypassRow = (fields) => {
    const indexToUse = this.state.maxBypassIndex + 1;
    fields.push({
      name: `index_${indexToUse}`,
      // NEW: store a structured selection (array) instead of CSV string
      bypass_affiliate: [],
      cap: null,
      failureProbability: null,
    });
    this.setState({
      nextBypassIndex: indexToUse + 1,
      maxBypassIndex: indexToUse,
    });
  };

  renderBypassRows = ({ fields }) => {
    const { currentCaps, qualityScoreCriteria } = this.props;

    return (
      <Box component='section' sx={{ mb: 1.5 }}>
        <Typography variant='h6' sx={{ mb: 2 }}>
          Bypass Affiliates
        </Typography>
        {fields.length > 0 ? (
          <Stack spacing={2}>
            {fields.map((fieldName, index) => {
              const fieldData = qualityScoreCriteria?.bypass?.[index];
              const nameValue = fieldData?.name || `index_${index}`;
              const redisIndex = nameValue.replace('index_', '');
              const currentCapValue = currentCaps ? currentCaps.bypass?.[redisIndex] || 0 : 0;
              const isLast = index === fields.length - 1;

              return (
                <Card key={fieldName}>
                  <CardContent>
                    <Stack
                      direction='row'
                      spacing={1}
                      sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
                    >
                      <Field
                        name={`${fieldName}.name`}
                        component={({ input }) => (
                          <Typography variant='body2'>{input.value || `index_${index}`}</Typography>
                        )}
                      />
                      <Stack direction='row' spacing={1}>
                        {isLast && (
                          <Button
                            color='primary'
                            type='button'
                            title='Add Bypass Affiliates Row'
                            startIcon={<Add />}
                            onClick={() => this.pushNewBypassRow(fields)}
                          >
                            Add Bypass
                          </Button>
                        )}
                        <Button
                          color='danger'
                          type='button'
                          title='Remove Bypass Affiliates Row'
                          startIcon={<Close />}
                          onClick={() => fields.remove(index)}
                        >
                          Remove
                        </Button>
                      </Stack>
                    </Stack>
                    <Grid container spacing={2} sx={{ alignItems: 'end', pb: 3 }}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <FormLabel htmlFor={`${fieldName}-affiliates`} sx={LABEL_SX}>
                          Affiliates (IDs or tags)
                        </FormLabel>
                        <AffiliatesIdsOrTagsPicker name={`${fieldName}.bypass_affiliate`} />
                      </Grid>
                      <Grid size={{ xs: 12, md: 2 }}>
                        <FormLabel htmlFor={`${fieldName}-cap`} sx={LABEL_SX}>
                          Cap Amount
                        </FormLabel>
                        <Field
                          name={`${fieldName}.cap`}
                          component={this.renderNumberInput}
                          id={`${fieldName}-cap`}
                          type='number'
                          placeholder='Infinity'
                          min={0}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 2 }}>
                        <FormLabel htmlFor={`${fieldName}-currentCap`} sx={LABEL_SX}>
                          Current Cap
                        </FormLabel>
                        <TextField
                          id={`${fieldName}-currentCap`}
                          value={currentCapValue}
                          disabled
                          slotProps={{ input: { readOnly: true } }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 2 }}>
                        <FormLabel htmlFor={`${fieldName}-failure`} sx={LABEL_SX}>
                          Failure Probability (%)
                        </FormLabel>
                        <Field
                          name={`${fieldName}.failureProbability`}
                          component={renderFailureProbabilityInput}
                          id={`${fieldName}-failure`}
                          placeholder='0%'
                          min={0}
                          max={0.99}
                          step='0.01'
                          validate={this.validateFailureProbability}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        ) : (
          <Box sx={{ textAlign: 'center' }}>
            <Button
              color='primary'
              type='button'
              title='Add Bypass Affiliates Row'
              startIcon={<Add />}
              onClick={() => this.pushNewBypassRow(fields)}
            >
              Add Bypass Affiliates Row
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  /* ---------------------------- Render ----------------------------- */
  render() {
    return (
      <TabPane tabId='Affiliate Quality Score'>
        <Stack spacing={3}>
          <FieldArray name='qualityScoreCriteria.qualityScoreRange' component={this.renderRangeRows} />

          <FieldArray name='qualityScoreCriteria.bypass' component={this.renderBypassRows} />

          <Box component='section' sx={{ mb: 1.5 }}>
            <Typography variant='h6' sx={{ mb: 2 }}>
              Cap Reset Configuration
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormLabel htmlFor='legacy-cap-reset-interval' sx={LABEL_SX}>
                  Reset Interval
                </FormLabel>
                <Field
                  name='qualityScoreCriteria.cap_reset.resetInterval'
                  component={this.renderSelectInput}
                  id='legacy-cap-reset-interval'
                  options={[{ value: 'daily', option: 'Daily' }]}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormLabel htmlFor='legacy-cap-reset-day-of-week' sx={LABEL_SX}>
                  Reset Day of Week
                </FormLabel>
                <Field
                  name='qualityScoreCriteria.cap_reset.resetDayOfWeek'
                  component={this.renderSelectInput}
                  id='legacy-cap-reset-day-of-week'
                  options={[{ value: '', option: 'Not applicable for daily reset' }]}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormLabel htmlFor='legacy-cap-reset-day-of-month' sx={LABEL_SX}>
                  Reset Day of Month
                </FormLabel>
                <Field
                  name='qualityScoreCriteria.cap_reset.resetDayOfMonth'
                  component={this.renderSelectInput}
                  id='legacy-cap-reset-day-of-month'
                  options={[{ value: '', option: 'Not applicable for daily reset' }]}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormLabel htmlFor='legacy-cap-reset-date' sx={LABEL_SX}>
                  Reset Date
                </FormLabel>
                <Field
                  name='qualityScoreCriteria.cap_reset.resetDate'
                  component={this.renderNumberInput}
                  id='legacy-cap-reset-date'
                  type='text'
                  placeholder='Not applicable for daily reset'
                  disabled
                />
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </TabPane>
    );
  }
}

export default AffiliateQualityScorePane;
